<template>
  <div class="pregled-racuna">

    <!-- Partner i dokument: lijevo tko, desno koji dokument i kada. -->
    <v-row dense>
      <v-col cols="12" sm="6">
        <div class="par"><span class="o">Partner</span>
          <span class="v velik">{{ mainledger.partnerName }}</span></div>
        <div class="par" v-if="mainledger.partnerVATID"><span class="o">OIB</span>
          <span class="v">{{ mainledger.partnerVATID }}</span></div>
        <div class="par" v-if="adresaPartnera"><span class="o">Adresa</span>
          <span class="v">{{ adresaPartnera }}</span></div>
        <div class="par" v-if="mainledger.partnerBankIBAN"><span class="o">IBAN</span>
          <span class="v">{{ mainledger.partnerBankIBAN }}</span></div>
      </v-col>

      <v-col cols="12" sm="6">
        <div class="par"><span class="o">Broj računa</span>
          <span class="v velik">{{ mainledger.InvoiceNumber }}</span></div>
        <div class="par"><span class="o">Datum računa</span>
          <span class="v">{{ locInvoiceDate }}</span></div>
        <div class="par"><span class="o">Datum knjiženja</span>
          <span class="v">{{ locBookingDate }}</span></div>
        <div class="par" v-if="mainledger.InvoicePaymentDeadlineDate">
          <span class="o">Dospijeće</span>
          <span class="v">{{ locInvoicePaymentDeadlineDate }}
            <span class="znak" :class="razredDospijeca">{{ tekstDospijeca }}</span>
          </span></div>
        <div class="par"><span class="o">Knjiga</span>
          <span class="v">{{ mainledger.BookName }}
            <span v-if="mainledger.BookID"> &middot; {{ locBookID }}</span>
          </span></div>
      </v-col>
    </v-row>

    <!-- IZNOSI: tablica s desno poravnatim brojevima. Nule su sive da se ne
         citaju kao podatak - na racunu s jednom stopom ostale su prazne. -->
    <div class="naslov-kartice mt-3">Iznosi
      <span class="desno">{{ mainledger.HomeCurrency || 'EUR' }}</span></div>

    <div class="pa-0">
      <table class="iznosi">
        <thead>
          <tr>
            <th>Stopa</th><th>Osnovica</th><th>PDV</th>
            <th>Pretporez</th><th>Bez prava</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="s in stope" :key="s.naziv">
            <td>{{ s.naziv }}</td>
            <td :class="{nula: !s.osnovica}">{{ localMoney(s.osnovica) }}</td>
            <td :class="{nula: !s.pdv}">{{ s.pdv === null ? '—' : localMoney(s.pdv) }}</td>
            <td :class="{nula: !s.canUse}">{{ s.canUse === null ? '—' : localMoney(s.canUse) }}</td>
            <td :class="{nula: !s.cantUse}">{{ s.cantUse === null ? '—' : localMoney(s.cantUse) }}</td>
          </tr>
          <tr class="zbroj">
            <td>Ukupno</td>
            <td>{{ localMoney(mainledger.AmountNetto) }}</td>
            <td>{{ localMoney(mainledger.AmountVAT) }}</td>
            <td>{{ localMoney(locCanUse) }}</td>
            <td>{{ localMoney(locCantUse) }}</td>
          </tr>
        </tbody>
      </table>

      <v-row dense class="mt-3 px-3 pb-3">
        <v-col cols="12" sm="6">
          <div class="par"><span class="o">Osnovica</span>
            <span class="v">{{ localMoney(mainledger.AmountNetto) }}</span></div>
          <div class="par"><span class="o">PDV</span>
            <span class="v">{{ localMoney(mainledger.AmountVAT) }}</span></div>
        </v-col>
        <v-col cols="12" sm="6">
          <div class="par"><span class="o">Ukupno računa</span>
            <span class="v velik">{{ localMoney(mainledger.InvoiceAmount) }}
              {{ mainledger.HomeCurrency || 'EUR' }}</span></div>
          <div class="par"><span class="o">Plaćeno</span>
            <span class="v">
              <span v-if="mainledger.paidClosed" class="znak ok">plaćeno
                <span v-if="mainledger.paidDate"> {{ localDate(mainledger.paidDate) }}</span>
              </span>
              <span v-else class="znak ceka">nije označeno kao plaćeno</span>
            </span></div>
        </v-col>
      </v-row>
    </div>

    <!-- Rijetko popunjeno stoji sklopljeno. Uz privitke stoji spajalica kad ih
         racun doista ima - da se ne mora otvarati skupina da bi se to vidjelo. -->
    <v-expansion-panels flat multiple class="sklopivo mt-2">
      <v-expansion-panel v-if="!hideCurrRate || mainledger.ForeignCurrency">
        <v-expansion-panel-header>
          Strana valuta
          <template v-slot:actions><v-icon small>mdi-chevron-down</v-icon></template>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-row dense>
            <v-col cols="12" sm="6">
              <div class="par"><span class="o">Valuta</span>
                <span class="v">{{ mainledger.ForeignCurrency || '—' }}</span></div>
              <div class="par"><span class="o">Tečaj</span>
                <span class="v">{{ mainledger.ExRate }}</span></div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="par"><span class="o">Iznos u valuti</span>
                <span class="v">{{ localMoney(mainledger.InvoiceForeginAmount) }}</span></div>
            </v-col>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-header>
          Skladište i otpremnica
          <template v-slot:actions><v-icon small>mdi-chevron-down</v-icon></template>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-row dense>
            <v-col cols="12" sm="6">
              <div class="par"><span class="o">Skladište</span>
                <span class="v">{{ mainledger.WareHouseId || '—' }}</span></div>
              <div class="par"><span class="o">Iznos ulaza</span>
                <span class="v">{{ mainledger.WareHouseEntryAmount
                  ? localMoney(mainledger.WareHouseEntryAmount) : '—' }}</span></div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="par"><span class="o">Broj otpremnice</span>
                <span class="v">{{ mainledger.DeliveryNoteNumber || '—' }}</span></div>
            </v-col>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-header>
          <!-- Naziv i spajalica u jednom omotu: inace header rasiri tekst
               preko cijele sirine i ikona zavrsi na sredini, gdje je nitko ne
               vidi. -->
          <span class="zaglavlje-skupine">
            Privitci i zapis
            <v-icon v-if="imaPrivitak" small color="green darken-2"
              title="Račun ima privitak">mdi-paperclip</v-icon>
          </span>
          <template v-slot:actions><v-icon small>mdi-chevron-down</v-icon></template>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-row dense>
            <v-col cols="12" sm="6">
              <div class="par"><span class="o">Datoteka</span>
                <span class="v">
                  {{ mainledger.MLFree2 || '—' }}
                  <!-- PDF se otvara tu, ispod - do sada se privitak vidio samo
                       na ekranu za uredivanje. -->
                  <v-btn
                    v-if="jePdf"
                    x-small text color="primary" class="ml-1"
                    @click="prikaziPdf = !prikaziPdf"
                  >{{ prikaziPdf ? 'Zatvori' : 'Otvori PDF' }}</v-btn>
                </span></div>
              <div class="par"><span class="o">Putanja</span>
                <span class="v">{{ mainledger.MLFree5 || '—' }}</span></div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="par"><span class="o">Upisao</span>
                <span class="v">{{ mainledger.email }}</span></div>
              <div class="par"><span class="o">Upisano</span>
                <span class="v">{{ locCreatedAt }}</span></div>
              <div class="par" v-if="locUpdatedAt"><span class="o">Zadnja izmjena</span>
                <span class="v">{{ locUpdatedAt }}</span></div>
            </v-col>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <DocumentPDFPreview v-if="prikaziPdf && mainledger.MLFree5" :fullpath="mainledger.MLFree5" />
  </div>
</template>

<script>
import MainLedgerService from '@/services/MainLedgerService'
import ledgerEN from './MainLedgerDescEN'
import ledgerHR from './MainLedgerDescHR'
import commEn from "@/language/commonEN"
import commHr from "@/language/commonHR"
import dayjs from 'dayjs'
import moneyFormat from '@/snippets/moneyFormat'
import DocumentPDFPreview from '../Document/DocumentPDFPreview'
import { uKunama } from '@/snippets/domacaValuta'

export default {
  name: 'mainledgerview2',
  $_veeValidate: {
    validator: 'new'
  },
  data () {
    return {
      menu: false,
      menu2: false,
      menu3: false,
      dateFormatted: null,
      dateFormatted2: null,
      modal: false,
      date1: null,
      date2: null,
      modal1: false,
      modal2: false,
      modal3: false,
      modal4: false,
      dialog1: false,
      dialog2: false,
      dialog3: false,
      dialog4: false,
      msg: '',
      valid: true,
      documentName: '',
      documentNameLoc: '',
      mainledger: { },
      mainledgerActiv: {},
      prikaziPdf: false,
      error: null,
      localError: false,
      newDocument: {},
      confpassword: '',
      size: 'sm',
      required: (value) => !!value || 'Required.',
      partners: [],
      employes: [],
      banks: [],
      currnecy: [],
      users: [],
      CompanyId: 0,
      selectPa: {},
      selectBank: {},
      selectCR: {},
      selectUs: {},
      mainQuery: {
        CompanyId: 1,
        BusinessYear: 0,
        documentName: '',
        MLType: 0
      },
      currnecyType: [
        { text: 'Kn', value: 'Kn' },
        { text: 'EUR', value: 'EUR' },
        { text: 'USD', value: 'USD' },
        { text: 'GBP', value: 'GBP' },
        { text: 'CHF', value: 'CHF' }
      ],
      bookType: [],
      money: {
        prefix: '= ',
        suffix: ' ',
        precision: 3
      },
      price: 7.500,
      locCreatedAt: '',
      locUpdatedAt: '',
      locBookingDate: '',
      locInvoiceDate: '',
      locInvoicePaymentDeadlineDate: '',
      locBookID: 0,
      locCanUse: 0,
      locCantUse: 0,
      mldetails: [],
      showTotals: true,
      lang: {},
      langC: {},
      haveDates: null,
      hideCurrRate: false
    }
  },
  components: {
    DocumentPDFPreview
  },
  async mounted () {
    try {
      if (this.$store.state.language === "EN") {
        this.lang = {...ledgerEN}
        this.langC = {...commEn}
      }
      if (this.$store.state.language === "HR") {
        this.lang = {...ledgerHR}
        this.langC = {...commHr}
      }
      this.mainledger = this.$store.state.mainledgerActiv ? this.$store.state.mainledgerActiv : {}
      this.documentName = this.$store.state.documentActiv
      this.documentNameLoc = this.$store.state.documentActivLoc
      if (this.$store.state.companyid) {
        this.CompanyId = await this.$store.state.companyid
        this.mainQuery.CompanyId = await this.$store.state.companyid
      }
      if (this.$store.state.businessYear) {
        this.mainQuery.BusinessYear = this.$store.state.businessYear
        this.hideCurrRate = uKunama(this.$store.state.businessYear)
      }
      if (this.$store.state.documentActiv) {
        this.mainQuery.documentName = this.$store.state.documentActiv
      }
      this.locCreatedAt = this.mainledger.createdAt ? dayjs(this.mainledger.createdAt).format('DD.MM.YYYY HH:mm') : ''
      this.locUpdatedAt = this.mainledger.updatedAt ? dayjs(this.mainledger.updatedAt).format('DD.MM.YYYY HH:mm') : ''
      this.haveDates = this.langC.CreatedAt + ' ' + this.locCreatedAt + ', ' + this.langC.UpdatedAt + ' ' + this.locUpdatedAt
      this.locBookingDate = dayjs(this.mainledger.BookingDate).format('DD.MM.YYYY')
      this.locInvoiceDate = dayjs(this.mainledger.InvoiceDate).format('DD.MM.YYYY')
      this.locInvoicePaymentDeadlineDate = dayjs(this.mainledger.InvoicePaymentDeadlineDate).format('DD.MM.YYYY')
      this.locBookID = parseInt(this.mainledger.BookID)
      this.locCanUse = parseFloat(this.mainledger.VAT1CanUse) + parseFloat(this.mainledger.VAT2CanUse) + parseFloat(this.mainledger.VAT3CanUse)
      this.locCantUse = parseFloat(this.mainledger.VAT1CanTUse) + parseFloat(this.mainledger.VAT2CanTUse) + parseFloat(this.mainledger.VAT3CanTUse)
      this.showTotals = this.$store.state.mainledgerTotals === 1 ? false : true 
    } catch (error) {
      // eslint-disable-next-line
      console.log('Error on mount:', error)
    }
  },
  computed: {
    adresaPartnera () {
      const m = this.mainledger || {}
      const ulica = [m.partnerStreet, m.partnerBuildingNameNumber].filter(x => x).join(' ')
      const mjesto = [m.partnerPostcode, m.partnerCity].filter(x => x).join(' ')
      return [ulica, mjesto].filter(x => x).join(', ')
    },
    // Redak po stopi; prikazuju se samo one koje racun stvarno ima. Mjereno na
    // 198.557 racuna: osnovna stopa 71,8 %, ostale 2-3 % - prikazivati ih uvijek
    // znacilo bi cetiri prazna retka na gotovo svakom racunu.
    stope () {
      const m = this.mainledger || {}
      const b = (v) => parseFloat(v) || 0
      const redci = []
      const dodaj = (naziv, base, amount, canUse, cantUse) => {
        if (!b(base) && !b(amount)) { return }
        redci.push({naziv: naziv, osnovica: b(base), pdv: b(amount),
          canUse: b(canUse), cantUse: b(cantUse)})
      }
      dodaj('25 %', m.VAT1Base, m.VAT1Amount, m.VAT1CanUse, m.VAT1CanTUse)
      dodaj('13 %', m.VAT2Base, m.VAT2Amount, m.VAT2CanUse, m.VAT2CanTUse)
      dodaj('5 %', m.VAT3Base, m.VAT3Amount, m.VAT3CanUse, m.VAT3CanTUse)
      // Oslobodenje i prijenos porezne obveze nemaju PDV ni pretporez - crtica
      // je iskrenija od nule.
      if (b(m.VAT4Base)) {
        redci.push({naziv: 'Oslobođeno / prijenos obveze', osnovica: b(m.VAT4Base),
          pdv: null, canUse: null, cantUse: null})
      }
      if (!redci.length) {
        redci.push({naziv: '—', osnovica: 0, pdv: 0, canUse: 0, cantUse: 0})
      }
      return redci
    },
    // Otvoriti se moze samo PDF; XML eRacuna se ne prikazuje u pregledniku.
    jePdf () {
      const m = this.mainledger || {}
      const putanja = String(m.MLFree5 || '')
      const naziv = String(m.MLFree2 || '')
      return /\.pdf$/i.test(putanja) || /\.pdf$/i.test(naziv)
    },
    // Privitak je datoteka uz racun (xml eRacuna ili pdf).
    imaPrivitak () {
      const m = this.mainledger || {}
      const ima = (v) => !!(v && String(v).trim())
      return ima(m.MLFree2) || ima(m.MLFree5) || ima(m.MLFree3)
    },
    danaDoDospijeca () {
      const d = this.mainledger ? this.mainledger.InvoicePaymentDeadlineDate : null
      if (!d) { return null }
      return dayjs(d).startOf('day').diff(dayjs().startOf('day'), 'day')
    },
    tekstDospijeca () {
      const n = this.danaDoDospijeca
      if (n === null) { return '' }
      if (this.mainledger && this.mainledger.paidClosed) { return '' }
      if (n < 0) { return 'kasni ' + Math.abs(n) + ' dana' }
      if (n === 0) { return 'danas' }
      return 'za ' + n + ' dana'
    },
    razredDospijeca () {
      const n = this.danaDoDospijeca
      if (n === null || (this.mainledger && this.mainledger.paidClosed)) { return 'ok' }
      return n < 0 ? 'kasni' : (n === 0 ? 'ceka' : 'ceka')
    },
    debitTotal () {
      return this.$store.state.mainledgerActiv.DebitTotal
    },
    creditTotal () {
      return this.$store.state.mainledgerActiv.CreditTotal
    },
    diffTotal () {
      return this.$store.state.mainledgerActiv.DiffTotal
    },
    closedAccount () {
      return this.$store.state.mainledgerActiv.ClosedAccount
    }
  },
  methods: {
    localMoney(moneyToFormat) {
        return moneyFormat(moneyToFormat)
      },
    localDate (d) {
      return d ? dayjs(d).format('DD.MM.YYYY') : ''
    },
    formatDate (date) {
      if (!date) {
        return null
      }
      const [year, month, day] = date.split('-')
      return `${day}.${month}.${year}`
    },
    parseDate (date) {
      if (!date) {
        return null
      }
      const [day, month, year] = date.split('.')
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
    },
    close () {
      this.show = false
    },
    async mainledgeragain () {
      try {
        this.$forceUpdate()
      } catch (err) {
        // eslint-disable-next-line
        console.log(err)
      }
    },
    async showPdf() {
      if (this.mainledger.MLFree5) {
        const showFile ={
          showFile: this.mainledger.MLFree5
        }
        await MainLedgerService.showPdf(showFile)
        .then(res => {
          const data = res.data
          var byteCharacters = atob(data);
          var byteNumbers = new Array(byteCharacters.length);
          for (var i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          var byteArray = new Uint8Array(byteNumbers);
          var blob = new Blob([byteArray], { type: "application/pdf" });

          const fileurl = window.URL.createObjectURL(blob)
          window.open(fileurl)
          
        }).catch(error => {
          this.text = 'Error ' + error
          this.color = 'red'
          this.snackbar = true
        })
      } else {
        alert('Sorry, document is not defined! Can not open.')
      }
    },
  },
  watch: {
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style >
/* Naziv skupine i oznaka uz njega drze se zajedno, lijevo; strelica za
   otvaranje ostaje desno. */
.pregled-racuna .zaglavlje-skupine {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
}

.pregled-racuna .par {
  display: grid;
  grid-template-columns: 150px 1fr;
  gap: 8px;
  padding: 3px 0;
  align-items: baseline;
}
.pregled-racuna .par .o { font-size: 12px; color: #5f6b7a; }
.pregled-racuna .par .v { font-weight: 500; }
.pregled-racuna .par .v.velik { font-size: 17px; }

.pregled-racuna .naslov-kartice {
  font-size: 17px;
  font-weight: 700;
  color: #1565c0;
  background: #eef4fb;
  border-bottom: 2px solid #1565c0;
  padding: 7px 12px;
  display: flex;
  align-items: center;
}
.pregled-racuna .naslov-kartice .desno {
  margin-left: auto;
  font-size: 12px;
  font-weight: 400;
  color: #5f6b7a;
}

/* Iznosi desno poravnati - inace se stupci brojeva ne mogu usporediti. */
.pregled-racuna table.iznosi { width: 100%; border-collapse: collapse; }
.pregled-racuna table.iznosi th {
  text-align: right;
  font-size: 11px;
  color: #5f6b7a;
  font-weight: 500;
  padding: 5px 12px;
  border-bottom: 1px solid #e3e8ee;
}
.pregled-racuna table.iznosi th:first-child { text-align: left; }
.pregled-racuna table.iznosi td {
  padding: 5px 12px;
  text-align: right;
  border-bottom: 1px solid #f0f3f6;
}
.pregled-racuna table.iznosi td:first-child { text-align: left; color: #5f6b7a; }
.pregled-racuna table.iznosi tr.zbroj td {
  font-weight: 700;
  border-top: 2px solid #1565c0;
  border-bottom: none;
}
/* Nula nije podatak - neka se ne cita kao da jest. */
.pregled-racuna .nula { color: #bdbdbd; }

.pregled-racuna .znak {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 11px;
  white-space: nowrap;
}
.pregled-racuna .znak.ok { background: #e8f5e9; color: #2e7d32; }
.pregled-racuna .znak.ceka { background: #fff3e0; color: #ef6c00; }
.pregled-racuna .znak.kasni { background: #ffebee; color: #c62828; }

.pregled-racuna .sklopivo >>> .v-expansion-panel-header {
  min-height: 40px;
  padding: 8px 12px;
  font-weight: 500;
  color: #1565c0;
}
.pregled-racuna .sklopivo >>> .v-expansion-panel { border-top: 1px solid #e3e8ee; }
.pregled-racuna .oznaka {
  font-size: 11px;
  font-weight: 400;
  color: #5f6b7a;
  background: #eceff1;
  padding: 1px 7px;
  border-radius: 9px;
  margin-left: 8px;
}

@media (max-width: 600px) {
  .pregled-racuna .par { grid-template-columns: 120px 1fr; }
}


</style>

